import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, Plus, ChevronRight, ShieldCheck, Search } from "lucide-react";
import { toast } from "sonner";
import { PortalLoginsUpload } from "@/components/PortalLogin";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function Clients() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const EMPTY = { legal_name: "", trade_name: "", gstin: "", username: "", password: "" };
  const [form, setForm] = useState(EMPTY);
  const [reg, setReg] = useState(null);
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const load = useCallback(() => api.get("/clients").then((r) => setClients(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const canManage = user?.role === "admin" || user?.role === "partner";

  const lookup = async () => {
    setLooking(true);
    try {
      const { data } = await api.get(`/gstin-lookup/${form.gstin.trim().toUpperCase()}`);
      setReg(data);
      setForm((f) => ({ ...f, legal_name: data.legal_name || f.legal_name, trade_name: data.trade_name || f.trade_name }));
      if (data.already_onboarded) { toast.warning("This GSTIN is already onboarded"); }
    } catch (e) { setReg(null); toast.error(apiError(e)); }
    setLooking(false);
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/clients", {
        legal_name: form.legal_name,
        trade_name: form.trade_name,
        gstin: form.gstin.trim().toUpperCase(),
        portal_login: { username: form.username, password: form.password },
        registration: reg || undefined,
      });
      toast.success("Client onboarded");
      setOpen(false);
      setForm(EMPTY); setReg(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight">Client Registry</h1>
          <p className="text-muted-foreground mt-1">Client GSTINs with securely stored GST portal credentials.</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
          <PortalLoginsUpload />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-client-button" className="flex items-center gap-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 text-sm font-medium transition-colors">
                <Plus className="h-4 w-4" /> Onboard Client
              </button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-head">Onboard client GSTIN</DialogTitle>
                <DialogDescription>Enter the GSTIN and fetch the registration details, then save the client's GST User ID and password (encrypted).</DialogDescription>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4" data-testid="client-form">
                <div>
                  <label className="small-label">GSTIN</label>
                  <div className="mt-1 flex gap-2">
                    <input data-testid="client-gstin-input" required value={form.gstin} maxLength={15}
                      onChange={(e) => { setForm({ ...form, gstin: e.target.value.toUpperCase() }); setReg(null); }}
                      className="w-full rounded-md bg-secondary/50 border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="27AABCA1234K1Z0" />
                    <button type="button" data-testid="client-gstin-fetch-button" onClick={lookup} disabled={looking || form.gstin.trim().length !== 15}
                      className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-border px-3 text-xs font-medium hover:bg-muted disabled:opacity-50">
                      <Search className="h-3.5 w-3.5" /> {looking ? "Fetching…" : "Fetch details"}
                    </button>
                  </div>
                </div>
                {reg && (
                  <div className="rounded-md border border-border bg-secondary/30 p-3 text-xs space-y-1" data-testid="client-registration-details">
                    {[["State", `${reg.state_code} · ${reg.state_name}`], ["PAN", reg.pan], ["Constitution", reg.constitution], ["Status", reg.status],
                      ["Registered on", reg.registration_date], ["Taxpayer type", reg.taxpayer_type], ["Address", reg.address]].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex gap-2"><span className="w-28 shrink-0 text-muted-foreground">{k}</span><span className="font-mono">{v}</span></div>
                    ))}
                    {reg.note && <p className="text-amber-600 pt-1" data-testid="client-registration-note">{reg.note}</p>}
                  </div>
                )}
                {[
                  ["legal_name", "Legal name", "Acme Traders Pvt Ltd"],
                  ["trade_name", "Trade name (optional)", "Acme"],
                ].map(([k, label, ph]) => (
                  <div key={k}>
                    <label className="small-label">{label}</label>
                    <input data-testid={`client-${k}-input`} required={k !== "trade_name"} value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="mt-1 w-full rounded-md bg-secondary/50 border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder={ph} />
                  </div>
                ))}
                <div className="rounded-md border border-border p-3 space-y-3">
                  <div className="flex items-center gap-2 small-label"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> GST login · encrypted at rest</div>
                  {[["username", "GST User ID", "text"], ["password", "GST Password (optional)", "password"]].map(([k, label, type]) => (
                    <input key={k} data-testid={`client-${k}-input`} value={form[k]} type={type} autoComplete="new-password"
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="w-full rounded-md bg-secondary/50 border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder={label} />
                  ))}
                </div>
                <button data-testid="client-submit-button" disabled={saving}
                  className="w-full rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white py-2.5 text-sm font-medium">
                  {saving ? "Saving…" : "Onboard"}
                </button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="grid gap-3">
        {clients.map((c) => (
          <button key={c.id} data-testid={`client-row-${c.id}`} onClick={() => nav(`/clients/${c.id}`)}
            className="flex items-center justify-between rounded-lg border border-border bg-card p-4 text-left hover:border-indigo-500/40 transition-colors">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-md bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-indigo-400" />
              </div>
              <div>
                <div className="font-head font-semibold">{c.legal_name}</div>
                <div className="font-mono text-xs text-muted-foreground">{c.gstin} · State {c.home_state}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {(c.gstin_configured || c.portal_login_saved) && <span className="text-xs font-mono text-emerald-400">GST login saved</span>}
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </button>
        ))}
        {clients.length === 0 && <div className="text-muted-foreground font-mono text-sm">No clients yet.</div>}
      </div>
    </div>
  );
}
